import type { AxiosRequestConfig } from 'axios';
import { API_BASE_URL } from '@/api/baseUrl';
import { resolveAnalyticsPagePath } from './routes';
import { getPerformanceReleaseAttributes } from './performance-config';
import {
  getPerformanceConsentGeneration,
  recordPerformanceTrace,
} from './performance-client';

const ENDPOINTS = [
  { path: /^\/bundle\/getInfosByChannelId$/, name: 'api_bundles_list' },
  { path: /^\/bundle\/filters$/, name: 'api_bundle_filters' },
  { path: /^\/projects\/getAll$/, name: 'api_projects_list' },
  { path: /^\/channels\/getByProjectId$/, name: 'api_channels_list' },
  { path: /^\/channels\/searchByProjectId$/, name: 'api_channels_search' },
  { path: /^\/projects\/[^/]+\/analytics$/, name: 'api_dashboard' },
  { path: /^\/projects\/[^/]+\/activity$/, name: 'api_activity' },
] as const;

type RequestTiming = {
  name: string;
  view: string;
  startTime: number;
  monotonicStart: number;
  generation: number;
};
const requests = new WeakMap<AxiosRequestConfig, RequestTiming>();

const getTraceName = (config: AxiosRequestConfig): string | undefined => {
  if ((config.method || 'get').toLowerCase() !== 'get' || !config.url) {
    return;
  }
  // Axios callers use relative paths. An absolute/overridden origin must never
  // be mistaken for the first-party API, even if its path happens to match.
  if (config.baseURL !== API_BASE_URL || !config.url.startsWith('/') || config.url.startsWith('//')) {
    return;
  }
  const path = config.url.split(/[?#]/, 1)[0];
  return ENDPOINTS.find(endpoint => endpoint.path.test(path))?.name;
};

export const startApiPerformanceTrace = (config: AxiosRequestConfig): void => {
  try {
    const generation = getPerformanceConsentGeneration();
    if (generation === null) return;
    const name = getTraceName(config);
    if (!name) return;
    requests.set(config, {
      name,
      view: resolveAnalyticsPagePath(window.location.pathname) || '/',
      startTime: Date.now(),
      monotonicStart: performance.now(),
      generation,
    });
  } catch {
    // Timing is optional, including when browser/storage APIs are unavailable.
  }
};

export const finishApiPerformanceTrace = (
  config: AxiosRequestConfig | undefined,
  status?: number,
  errorCode?: string,
  failed = false
): void => {
  try {
    if (!config) return;
    const timing = requests.get(config);
    if (!timing) return;
    requests.delete(config);
    const outcome = errorCode === 'ERR_CANCELED'
      ? 'cancelled'
      : errorCode === 'ECONNABORTED' || errorCode === 'ETIMEDOUT'
        ? 'timeout'
        : failed
          ? status ? 'http_error' : 'network_error'
          : 'success';
    recordPerformanceTrace({
      name: timing.name,
      startTime: timing.startTime,
      duration: performance.now() - timing.monotonicStart,
      attributes: {
        method: 'GET',
        outcome,
        status: typeof status === 'number' && Number.isInteger(status) && status >= 100 && status <= 599 ? String(status) : 'none',
        view: timing.view,
        ...getPerformanceReleaseAttributes(),
      },
    }, timing.generation);
  } catch {
    // Do not alter response/error, cancellation, logout or toast behavior.
  }
};
